'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import type {
  QuizAttemptPublicDto,
  QuizAttemptResultDto,
  QuizAnswerInput,
} from '@peach/types/quizzes/quiz-attempt.dto.js';
import {
  startQuizAttempt,
  updateQuizAttempt,
  completeQuizAttempt,
  claimQuizAttempt,
  getQuizResult,
} from '@/app/actions/quizzes';
import {
  recordQuizAttemptStarted,
  recordQuizAttemptCompleted,
  clearPendingQuizClaim,
} from '@/lib/quiz-claim';
import { useHeldWork } from '@/components/system/reload-safety';
import { playAnswerBlip, playQuizComplete } from '@/lib/utils/quizSounds';
import { useQuizSlam, SLAM_MS } from '@/components/quiz/QuizSlam';
import type { SanitizedQuiz } from '@/lib/quiz/sanitize';

export type QuizPhase = 'inline' | 'playing' | 'finishing' | 'gate' | 'result';

/** A missed question, with its right answer only when the server sent one (a member). */
export interface MissedQuestion {
  /** 1-based, in the order the player was dealt. */
  number: number;
  text: string;
  answer: string | null;
}

const FLASH_MS = 130;

/**
 * Playing a quiz, whatever draws it: the attempt, the answers, the finish, the sign-in
 * gate and the result. The storefront's card and a subscription page's post both read
 * this, so a play is recorded one way wherever it happens.
 */
export function useQuizPlay({
  quiz,
  isOwner,
  tutorUsername,
  loggedIn,
  dmReplyId = null,
}: {
  quiz: SanitizedQuiz;
  isOwner: boolean;
  tutorUsername: string;
  loggedIn: boolean;
  dmReplyId?: string | null;
}) {
  const router = useRouter();
  const [phase, setPhase] = useState<QuizPhase>('inline');
  const { slam, fire: fireSlam, clear: clearSlam } = useQuizSlam(quiz.totalQuestions);
  const hold = useHeldWork((state) => state.hold);
  useEffect(() => {
    if (phase === 'inline' || phase === 'result') return;
    return hold();
  }, [phase, hold]);
  const [attempt, setAttempt] = useState<QuizAttemptPublicDto | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [result, setResult] = useState<QuizAttemptResultDto | null>(null);
  const [flash, setFlash] = useState<{ optionId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const answersRef = useRef<QuizAnswerInput[]>([]);
  const questionShownAtRef = useRef<number>(Date.now());
  const startedAtRef = useRef<number>(Date.now());
  const busyRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);

  const questions = attempt ? attempt.questions : quiz.questions;
  const total = quiz.totalQuestions;

  /**
   * Every step of the quiz funnel carries the same identity, so shown → started
   * → completed → gate can be read per quiz and per provider without a join.
   * The lead itself is not tracked here: it is the `auth_completed` the gate's
   * InlineAuth fires, so one event means "signed up" everywhere on the page.
   */
  const track = useCallback(
    (event: string, props?: Record<string, unknown>) => {
      posthog.capture(event, {
        quiz_id: quiz.id,
        provider_username: tutorUsername,
        creator_username: tutorUsername,
        tutor_username: tutorUsername,
        logged_in: loggedIn,
        is_owner: isOwner,
        total_questions: total,
        ...props,
      });
    },
    [quiz.id, isOwner, tutorUsername, loggedIn, total]
  );

  // The impression every rate is measured against: the card was on the page.
  // Once per mount, before any interaction.
  useEffect(() => {
    track('quiz_card_shown');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishQuiz = useCallback(
    async (attemptId: string, answers: QuizAnswerInput[]) => {
      if (cardRef.current) setLockedHeight(cardRef.current.offsetHeight);
      setPhase('finishing');
      const slamShownAt = Date.now();
      const elapsedMs = slamShownAt - startedAtRef.current;
      const completed = await completeQuizAttempt(attemptId, { answers, elapsedMs });
      if (!completed.success) {
        setError('Could not save your result — try again');
        setPhase('playing');
        return;
      }
      // A result that beats the animation is not a reason for the slam to flash.
      const held = Date.now() - slamShownAt;
      if (held < SLAM_MS) {
        await new Promise((resolve) => window.setTimeout(resolve, SLAM_MS - held));
      }
      setResult(completed.data);
      track('quiz_completed', {
        score: completed.data.score,
        wrong_count: completed.data.totalQuestions - completed.data.score,
        top_percentile: completed.data.topPercentile,
        elapsed_ms: elapsedMs,
      });
      if (loggedIn) {
        setPhase('result');
        return;
      }
      recordQuizAttemptCompleted(quiz.id, attemptId);
      // The ask, separated from the answer to it: a completion that never
      // reaches this is a different loss from one that refuses the gate.
      track('quiz_gate_shown');
      setPhase('gate');
    },
    [loggedIn, quiz.id, track]
  );

  const openScore = useCallback(async () => {
    if (attempt) {
      await claimQuizAttempt(attempt.id);
      clearPendingQuizClaim(quiz.id);
      // Read again as the signed-in player: a member's result carries the answers.
      const again = await getQuizResult(attempt.id);
      if (again.success) setResult(again.data);
    }
    setPhase('result');
  }, [attempt, quiz.id]);

  const onGateSuccess = useCallback(async () => {
    await openScore();
    router.refresh();
  }, [openScore, router]);

  // A page can carry several quizzes, and the gate is per card: signing in on
  // one re-renders the tree with `loggedIn` true, and the others must open
  // their own score rather than keep asking. Only the card that was signed in
  // on refreshes — one sign-in is one refresh, however many cards react to it.
  useEffect(() => {
    if (phase === 'gate' && loggedIn) void openScore();
  }, [phase, loggedIn, openScore]);

  const handleAnswer = useCallback(
    async (optionId: string) => {
      if (busyRef.current || flash) return;
      busyRef.current = true;
      setError(null);
      try {
        const tappedAt = Date.now();
        playAnswerBlip(answersRef.current.length);
        setFlash({ optionId });
        setPhase('playing');

        let liveAttempt = attempt;
        if (!liveAttempt) {
          const started = await startQuizAttempt(quiz.id, dmReplyId);
          if (!started.success) {
            setFlash(null);
            setError('Could not start the quiz — try again');
            return;
          }
          liveAttempt = started.data;
          startedAtRef.current = tappedAt;
          // The first answer is the start: an attempt is a question answered.
          track('quiz_started');
          if (!loggedIn) recordQuizAttemptStarted(quiz.id, liveAttempt.id);
        }

        const dealt = liveAttempt.questions;
        const answer: QuizAnswerInput = {
          questionId: dealt[currentIdx]!.id,
          optionId,
          timeMs: tappedAt - questionShownAtRef.current,
        };
        answersRef.current = [...answersRef.current, answer];

        const isLast = answersRef.current.length >= total;
        const attemptId = liveAttempt.id;
        const answers = answersRef.current;

        window.setTimeout(
          () => {
            setFlash(null);
            // Swapped in as the card moves on, never under the finger: the teaser
            // question came from the page payload and the attempt deals its own
            // order, so adopting it a moment early would reshuffle the options
            // being answered.
            setAttempt(liveAttempt);
            if (isLast) {
              playQuizComplete();
              void finishQuiz(attemptId, answers);
              return;
            }
            setCurrentIdx((i) => i + 1);
            questionShownAtRef.current = Date.now();

            fireSlam(currentIdx);
            // Every answer, not every third: the exact question an abandoned
            // attempt died on is analytics the throttle used to blur away.
            void updateQuizAttempt(attemptId, {
              answers,
              currentIndex: answers.length,
            });
          },
          Math.max(0, FLASH_MS - (Date.now() - tappedAt))
        );
      } finally {
        busyRef.current = false;
      }
    },
    [attempt, currentIdx, flash, total, fireSlam, quiz.id, loggedIn, track, finishQuiz, dmReplyId]
  );

  // What the result says about each question, in the order the player was dealt.
  const marks = result?.answers.map((a) => a.isCorrect) ?? [];
  const missed: MissedQuestion[] = (result?.answers ?? []).flatMap((a, i) => {
    if (a.isCorrect) return [];
    const question = questions.find((q) => q.id === a.questionId);
    if (!question) return [];
    const right = question.options.find((o) => a.correctOptionIds.includes(o.id));
    return [{ number: i + 1, text: question.text, answer: right?.label ?? null }];
  });

  return {
    phase,
    questions,
    currentIdx,
    total,
    flash,
    error,
    result,
    marks,
    missed,
    /** The server sent this player the answers: they are a member. */
    answered: missed.some((m) => m.answer !== null),
    handleAnswer,
    onGateSuccess,
    cardRef,
    lockedHeight,
    slam,
    clearSlam,
  };
}
